import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import history from 'app-router/history';
import { Col, Row } from 'antd';

import { SendCommunicationChannels, SendCommunicationSecondStepForm } from 'types';
import {
  SendCommunicationSelectors,
  SendCommunicationActions,
  reasonsSelectors,
  reasonsActions,
  MessagesMasksSelectors,
  MessagesMasksActions,
  MasksActions,
  MasksConfigActions,
  MasksSelectors,
  MasksConfigSelectors,
  helpInfosSelectors,
  helpInfosActions,
  SituationsSelectors,
  SituationsActions,
  UserInfoSelectors,
  compensationSentencesActions,
  compensationSentencesSelectors,
  servicesPassengersConcernedByCommunicationSelectors,
  ClearStateSliceActions,
  ServiceTypesSelectors,
} from 'common/config/store';
import { useIntl } from 'common/config/i18n';
import HeaderWithSteps from 'components/organisms/header-with-steps';
import ReminderSelections from 'components/organisms/reminder-selections';
import ServicesPassengersConcernedByCommunication from 'components/organisms/total-impacted-passengers';
import Button from 'components/molecules/button';
import { StyledBlueLink, StyledFormElems } from 'common/styling/mixins';
import { getTheme } from 'common/styling/theme';
import {
  prepareSaveCommunicationsParams,
  getHasCompensation,
  calculateSubtitle,
} from 'common/utils/send-communication';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import TargetSituation from 'components/organisms/target-situation';
import TargetChannels from 'components/organisms/target-channels';
import EmailContextualization from 'components/organisms/email-contextualization';
import ImpactedStations from 'components/organisms/impacted-stations';
import TargetSignature from 'components/organisms/target-signature';
import MessageAssistant from 'components/organisms/message-assistant';
import { Validators } from 'core';
import { isEspagne } from 'common/config/env';
import { useOutsideClickDetector } from 'hooks/use-breakpoint';
import {
  ButtonWrapper,
  ChannelsArePresentKeyType,
  ConfirmCompensationModal,
  PlaceHolderModal,
  MsgsPreviewModal,
  Props,
  SelectionWrapper,
  VerticalSpace,
  Wrapper,
  formikInitialValues,
  isCommunicationContentHasPlaceholders,
  StickyBlock,
  isCommunicationContentHasProhibitedLinks,
  ProhibitedLinksModal,
} from './utils';

const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_MAX_RESULT_PAGE = 300;

const SendCommunicationStepTwo: React.FC<Props> = ({ match }) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const observer = useRef<ResizeObserver | null>(null);
  const reminderSelectionsRef = useRef<HTMLDivElement>(null);
  const targetChannelsRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const impactedStationsRef = useRef<HTMLFormElement>(null);
  const messageAssistantRef = useRef<HTMLDivElement>(null);
  const primaryEditorWrapperRef = useRef<HTMLDivElement>(null);
  const secondaryEditorWrapperRef = useRef<HTMLDivElement>(null);
  const tertiaryEditorWrapperRef = useRef<HTMLDivElement>(null);

  const [reminderSelectionsHeight, setReminderSelectionsHeight] = useState<number>(0);
  const [targetChannelsHeight, setTargetChannelsHeight] = useState<number>(0);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [focusedTextFieldIndex, setFocusedTextFieldIndex] = useState<{
    contentIndex: number;
    isPrimary: boolean;
    isSecondary: boolean;
    isTertiary: boolean;
  }>();
  const [showMsgsPreview, setShowMsgsPreview] = useState<boolean>(false);
  const [showConfirmCompensation, setShowConfirmCompensation] = useState<boolean>(false);
  const [showPlaceholderModal, setShowPlaceholderModal] = useState<boolean>(false);
  const [showProhibitedLinksModal, setShowProhibitedLinksModal] = useState<boolean>(false);
  const [isMessageAssistantActive, setIsMessageAssistantActive] = useState<boolean>(false);
  const [defaultPrincipalZoneMessages, setDefaultPrincipalZoneMessages] = useState<
    | {
        principalZoneLongMessageSaved: string;
        principalZoneShortMessageSaved: string;
      }
    | undefined
  >(undefined);
  const [overviewOD, setOverviewOD] = useState<string[]>([]);

  const compensationSentencesData = useSelector(compensationSentencesSelectors.dataSelector);
  const hasInformations = useSelector(MessagesMasksSelectors.hasInformationsSelector);
  const servicesPassengersConcernedByCommunicationData = useSelector(
    servicesPassengersConcernedByCommunicationSelectors.ServicesPassengersConcernedByCommunicationDataSelector
  );
  const servicesPassengersConcernedByCommunicationStatus = useSelector(
    servicesPassengersConcernedByCommunicationSelectors.ServicesPassengersConcernedByCommunicationStatusSelector
  );
  const isBulkCommunication = useSelector(SendCommunicationSelectors.isBulkCommunicationSelector);
  const selectedServices = useSelector(SendCommunicationSelectors.selectedServicesSelector);
  const maxDaysBeforeDeferredSending = useSelector(SendCommunicationSelectors.maxDaysBeforeDeferredSendingSelector);
  const stationsConcerned = useSelector(SendCommunicationSelectors.stationsConcernedSelector);
  const saveCommunicationError = useSelector(SendCommunicationSelectors.saveCommunicationErrorSelector);
  const isSaveCommunicationLoading =
    useSelector(SendCommunicationSelectors.saveCommunicationStatusSelector) === 'loading';
  const formValuesFirstStep = useSelector(SendCommunicationSelectors.firstStepFormValuesSelector);
  const formValuesSecondStep = useSelector(SendCommunicationSelectors.secondStepFormValuesSelector);

  const situationsByClientType = useSelector(MessagesMasksSelectors.situationsByClientTypeSelector);
  const reasonsData = useSelector(reasonsSelectors.reasonsSelector);
  const selectedMessageMask = useSelector(MessagesMasksSelectors.selectedMessageMaskSelector);
  const selectedMask = useSelector(MasksSelectors.selectedMaskSelector);
  const masksConfig = useSelector(MasksConfigSelectors.masksConfigSelector);
  const situations = useSelector(SituationsSelectors.situationsSelector);
  const fullUserName = useSelector(UserInfoSelectors.fullNameSelector);
  const canWriteDefferedCommunications = useSelector(UserInfoSelectors.hasWriteAccess('Deffered-Communications'));
  const dataMessageAssistant = useSelector(helpInfosSelectors.dataMessageAssistantSelector);
  const serviceTypes = useSelector(ServiceTypesSelectors.serviceTypesSelector);
  const selectedServiceTypeID = useSelector(SendCommunicationSelectors.selectedServiceTypeIDSelector);

  const { situationCategory } = match.params;

  const handleCompensationAndSubmit = (values: SendCommunicationSecondStepForm) => {
    const hasCompensationZone = getHasCompensation(formValuesFirstStep.customersSelection, selectedMessageMask);
    if (isEspagne && !values.compensationSelected && hasCompensationZone) {
      setShowConfirmCompensation(true);
    } else {
      dispatch(SendCommunicationActions.submitStep2(values));
      setShowMsgsPreview(true);
    }
  };

  const formik = useFormik({
    initialValues: formikInitialValues,
    validate: (values) => {
      const defaultReason = reasonsData?.find((reason) => !!reason.isNotEditable);
      return Validators.sendCommunication.SecondStep(values, {
        isOthersReason: !!(values.reasonId === defaultReason?.id),
        isReasonActive: !!selectedMessageMask?.hasReason,
        isVolumeDelay: !!selectedMessageMask?.hasVolumeDelay,
        isSubtitutionAndSchedule: !!selectedMessageMask?.hasSubstituteStation,
        isDock: !!selectedMessageMask?.hasDock,
        isSignatureActive: !!selectedMessageMask?.hasSigningZone,
      });
    },
    onSubmit: (values) => {
      if (!isCommunicationContentHasPlaceholders(values) && !isCommunicationContentHasProhibitedLinks(values)) {
        handleCompensationAndSubmit(values);
      } else {
        if (isCommunicationContentHasPlaceholders(values)) {
          setShowPlaceholderModal(true);
        } else if (isCommunicationContentHasProhibitedLinks(values)) {
          setShowProhibitedLinksModal(true);
        }
      }
    },
  });

  const selectedSituations = useMemo(() => {
    const situations = situationsByClientType?.[0]?.situations;
    const defaultSituation = situations?.find((situation) => !!situation.isNotEditable);
    return {
      default: defaultSituation,
      data: [
        {
          id: 'selectedSituations',
          list: situations?.map((situation) => ({ id: situation.id, name: situation.name })) || [],
        },
      ],
    };
  }, [situationsByClientType]);

  const reasons = useMemo(() => {
    const defaultReason = reasonsData?.find((reason) => !!reason.isNotEditable);
    return {
      isOthersReason: defaultReason?.id === formik.values.reasonId,
      data: [
        {
          id: 'reasons',
          list: reasonsData?.map((reason) => ({ id: reason.id, name: reason.name })) || [],
        },
      ],
    };
  }, [reasonsData, formik.values.reasonId]);

  const currentTemplate = useMemo(() => {
    return selectedMask?.templates.find(
      (template) => template?.clientTypeId === formValuesFirstStep.customersSelection
    );
  }, [selectedMask, formValuesFirstStep]);

  const isMailTargeted = useMemo(() => {
    return formik?.values?.communicationChannels?.includes(1);
  }, [formik?.values?.communicationChannels]);

  const communicationChannelsUtils = useMemo(() => {
    const communicationChannelsDefaultValue: Array<number> = [];
    const channelsDefaultValues = {
      mail: !!currentTemplate?.mailConfiguration?.enabledByDefault,
      sms: !!currentTemplate?.smsConfiguration?.enabledByDefault,
      whatsApp: !!currentTemplate?.whatsappConfiguration?.enabledByDefault,
      flashInfo: !!currentTemplate?.flashEnabled,
    };
    const channelsArePresent = {
      mail: !!masksConfig?.mail && !!currentTemplate?.mailConfiguration?.fileName,
      sms: !!masksConfig?.sms && !!currentTemplate?.smsConfiguration?.fileName,
      whatsApp: !!masksConfig?.whatsApp && !!currentTemplate?.whatsappConfiguration?.refFileName,
      flashInfo: !!masksConfig?.flashInfo,
    };
    if (channelsArePresent.mail && channelsDefaultValues.mail) {
      communicationChannelsDefaultValue.push(SendCommunicationChannels.Mail);
    }
    if (channelsArePresent.sms && channelsDefaultValues.sms) {
      communicationChannelsDefaultValue.push(SendCommunicationChannels.Sms);
    }
    if (channelsArePresent.whatsApp && channelsDefaultValues.whatsApp) {
      communicationChannelsDefaultValue.push(SendCommunicationChannels.WhatsApp);
    }
    if (channelsArePresent.flashInfo && channelsDefaultValues.flashInfo) {
      communicationChannelsDefaultValue.push(SendCommunicationChannels.FlashInfo);
    }

    return { communicationChannelsDefaultValue, channelsArePresent };
  }, [masksConfig, currentTemplate]);

  /**
   * Trigger the validation whenever formik.values change
   * In order to pass the updated value to the validate method
   */
  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  useEffect(() => {
    dispatch(
      MessagesMasksActions.fetchSituationsByClientType({
        clientTypeId: formValuesFirstStep.customersSelection,
        situationCategoryId: situationCategory === 'PRE_OP' ? 1 : 2,
      })
    );
    dispatch(reasonsActions.fetchReasons());
    dispatch(MasksConfigActions.fetchMasksConfig());
    dispatch(SituationsActions.fetchSituations());
    dispatch(compensationSentencesActions.fetchCompensationSentences());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Observing the header's height changes --------------------------
  useEffect(() => {
    observer.current = new ResizeObserver((entries) => {
      const newHeight = entries?.[0]?.contentRect?.height;
      if (newHeight && newHeight !== headerHeight) {
        setHeaderHeight(newHeight);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (headerRef.current) {
      observer.current?.observe(headerRef.current);
    }
    return () => {
      observer.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRef.current]);
  // -----------------------------------------------------------------

  useEffect(() => {
    if (reminderSelectionsRef?.current?.clientHeight) {
      setReminderSelectionsHeight(reminderSelectionsRef?.current?.clientHeight);
    }
  }, [reminderSelectionsHeight, reminderSelectionsRef]);

  useEffect(() => {
    if (targetChannelsRef?.current?.clientHeight) {
      setTargetChannelsHeight(targetChannelsRef?.current?.clientHeight);
    }
  }, [reminderSelectionsHeight, targetChannelsRef]);

  useEffect(() => {
    if (selectedSituations.default?.id) {
      dispatch(MessagesMasksActions.fetchMessageMask(selectedSituations.default.id.toString()));
      dispatch(MasksActions.fetchMask(selectedSituations.default.id.toString()));
      getDataMessageAssisant(
        {
          serviceTypeIDValue: selectedServiceTypeID,
          situationIDValue: selectedSituations.default?.id,
        },
        true
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedSituations, selectedServiceTypeID]);

  useEffect(() => {
    formik.setFieldValue('situationId', selectedSituations.default?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSituations.default]);

  useEffect(() => {
    formik.setFieldValue('communicationChannels', communicationChannelsUtils.communicationChannelsDefaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communicationChannelsUtils.communicationChannelsDefaultValue]);

  useEffect(() => {
    if (isMailTargeted) {
      formik.setFieldValue('mailObject', currentTemplate?.mailConfiguration?.defaultObject || '');
      formik.setFieldValue('mailHeadband', currentTemplate?.mailConfiguration?.defaultHeadband || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMailTargeted]);

  useEffect(() => {
    formik.setFieldValue('signature', selectedMessageMask?.signingContent || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMessageMask]);

  useEffect(() => {
    const handleOverviewOD = () => {
      const sendCommunicationStepTwoState = history.location.state as {
        odsSummaryContent: string[][];
      } | null;
      if (sendCommunicationStepTwoState?.odsSummaryContent) {
        const overviewOD = sendCommunicationStepTwoState.odsSummaryContent.map((item) => item.join(' ; '));
        setOverviewOD(overviewOD);
        // Clear the history state to avoid unintended persistence
        history.replace(history.location.pathname, {});
      }
    };

    handleOverviewOD();
  }, []);

  const getDataMessageAssisant = (
    {
      situationIDValue,
      subFamilyValue,
      serviceTypeIDValue,
    }: {
      situationIDValue?: number;
      subFamilyValue?: string;
      serviceTypeIDValue?: number;
    },
    updateDefaultPrincipalZoneMessages?: boolean
  ) => {
    dispatch(
      helpInfosActions.fetchHelpInfosMessageAssisant(
        {
          selectedServiceTypes: serviceTypeIDValue ? [serviceTypeIDValue] : undefined,
          SituationIDs: situationIDValue ? [situationIDValue] : undefined,
          SubFamily: subFamilyValue,
          pageNumber: DEFAULT_PAGE_NUMBER,
          maxResultPage: DEFAULT_MAX_RESULT_PAGE,
          IsExactFilterSubFamily: true,
        },
        (response) => {
          if (!response.cancelled && !response.error) {
            if (updateDefaultPrincipalZoneMessages) {
              if (response.data?.result?.length === 1) {
                setDefaultPrincipalZoneMessages({
                  principalZoneLongMessageSaved: response.data.result[0].longMessage,
                  principalZoneShortMessageSaved: response.data.result[0].shortMessage,
                });
              } else {
                setDefaultPrincipalZoneMessages(undefined);
              }
            }
          }
        }
      )
    );
  };

  const handleSelectSituation = (situationId: number) => {
    formik.resetForm();
    formik.setValues({ ...formikInitialValues, situationId: situationId });
    if (situationId) {
      dispatch(MessagesMasksActions.fetchMessageMask(situationId.toString()));
      dispatch(MasksActions.fetchMask(situationId.toString()));
      getDataMessageAssisant(
        {
          serviceTypeIDValue: selectedServiceTypeID,
          situationIDValue: situationId,
        },
        true
      );
    }
  };

  const handleSelectReason = (reasonId: number) => {
    formik.setFieldValue('otherReasonComplementary', '');
    formik.setFieldValue('reasonId', reasonId);
  };

  const handleChannelChange = (e: CheckboxChangeEvent, id: SendCommunicationChannels) => {
    let newCommunicationChannels: Array<number> = [];
    if (formik.values.communicationChannels?.includes(id)) {
      if (id === 1) {
        if (!currentTemplate?.mailConfiguration?.defaultHeadband?.length) {
          formik.setFieldValue('mailHeadband', undefined);
        }
        if (!currentTemplate?.mailConfiguration?.defaultObject?.length) {
          formik.setFieldValue('mailObject', undefined);
        }
      }
      formik.values.communicationChannels?.map((channel) => {
        if (channel !== id || (channel === id && !!e.target.checked)) {
          newCommunicationChannels.push(channel);
        }
      });
    } else {
      if (e.target.checked) {
        if (formik.values.communicationChannels?.length) {
          newCommunicationChannels = [...formik.values.communicationChannels];
        }
        newCommunicationChannels.push(id);
      }
    }

    formik.setFieldValue('communicationChannels', newCommunicationChannels);
  };

  const onFocusTextField = (
    contentIndex: number,
    isPrimary: boolean,
    isSecondary: boolean,
    isTertiary: boolean
  ): void => {
    setFocusedTextFieldIndex({ contentIndex, isPrimary, isSecondary, isTertiary });
    setIsMessageAssistantActive(true);
  };

  useOutsideClickDetector(
    messageAssistantRef,
    () => {
      setIsMessageAssistantActive(false);
    },
    primaryEditorWrapperRef,
    secondaryEditorWrapperRef,
    tertiaryEditorWrapperRef
  );

  const handleCopyText = (longMessage: string, shortMessage?: string): void => {
    if (focusedTextFieldIndex?.isPrimary) {
      // Set the principal longMessage
      impactedStationsRef?.current?.insertText(longMessage, true, false, false);
      // Set the corresponding principal shortMessage if there is one
      if (formik?.values?.communicationContent && selectedMessageMask?.hasPrincipalZoneShortMessage) {
        formik.setFieldValue(
          `communicationContent[${focusedTextFieldIndex?.contentIndex}].principalZoneShortMessage`,
          shortMessage
        );
      }
    } else if (focusedTextFieldIndex?.isSecondary) {
      // Set the secondary longMessage
      impactedStationsRef?.current?.insertText(longMessage, false, true, false);
      // Set the corresponding secondary shortMessage if there is one
      if (formik?.values?.communicationContent && selectedMessageMask?.hasSecondaryZoneShortMessage) {
        formik.setFieldValue(
          `communicationContent[${focusedTextFieldIndex?.contentIndex}].secondaryZoneShortMessage`,
          shortMessage
        );
      }
    } else if (focusedTextFieldIndex?.isTertiary) {
      impactedStationsRef?.current?.insertText(longMessage, false, false, true);
    }
  };

  const saveCommunication = () => {
    if (selectedServices && formValuesFirstStep && formValuesSecondStep) {
      const isBulk = !!isBulkCommunication;
      const selectedServicesMapped = selectedServices.map(({ serviceName, circulationDate }) => ({
        serviceName,
        serviceDate: circulationDate,
      }));
      const saveCommunicationsParams = prepareSaveCommunicationsParams({
        selectedServices: selectedServicesMapped,
        formValuesFirstStep,
        formValuesSecondStep,
        stationsConcerned,
        fullName: fullUserName,
        isBulkCommunication: isBulk,
        maxDaysBeforeDeferredSending,
        overviewOD,
      });
      dispatch(
        SendCommunicationActions.saveCommunications(saveCommunicationsParams, (result) => {
          if (!result.cancelled && !result.error) {
            setShowMsgsPreview(false);
            history.push('/send-communication-confirmation');
          }
        })
      );
    }
  };

  const hasTargetChannels = !!Object.keys(communicationChannelsUtils.channelsArePresent).find(
    (key) => communicationChannelsUtils.channelsArePresent[key as ChannelsArePresentKeyType]
  );

  const subtitle = calculateSubtitle({
    selectedServices,
    translate: t,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      {showPlaceholderModal && (
        <PlaceHolderModal
          open={showPlaceholderModal}
          onCancel={() => setShowPlaceholderModal(false)}
          onConfirm={(isConfirm: boolean) => {
            setShowPlaceholderModal(false);
            if (isConfirm) {
              if (isCommunicationContentHasProhibitedLinks(formik.values)) {
                setShowProhibitedLinksModal(true);
              } else {
                handleCompensationAndSubmit(formik.values);
              }
            }
          }}
        />
      )}
      {showProhibitedLinksModal && (
        <ProhibitedLinksModal
          open={showProhibitedLinksModal}
          onCancel={() => setShowProhibitedLinksModal(false)}
          onConfirm={(isConfirm: boolean) => {
            setShowProhibitedLinksModal(false);
            if (isConfirm) {
              handleCompensationAndSubmit(formik.values);
            }
          }}
        />
      )}
      {showConfirmCompensation && (
        <ConfirmCompensationModal
          open={showConfirmCompensation}
          onCancel={() => setShowConfirmCompensation(false)}
          onConfirm={(isConfirm: boolean) => {
            setShowConfirmCompensation(false);
            if (isConfirm) {
              dispatch(SendCommunicationActions.submitStep2(formik.values));
              setShowMsgsPreview(true);
            }
          }}
        />
      )}
      {showMsgsPreview && (
        <MsgsPreviewModal
          open={showMsgsPreview}
          onCancel={() => {
            setShowMsgsPreview(false);
            dispatch(
              ClearStateSliceActions.clearStateSlice({
                sliceReducer: 'SendCommunication',
                oneLevelNestedPathKeys: ['saveCommunication', 'error'],
              })
            );
            dispatch(
              ClearStateSliceActions.clearStateSlice({
                sliceReducer: 'SendCommunication',
                oneLevelNestedPathKeys: ['saveCommunication', 'status'],
              })
            );
          }}
          onConfirm={() => {
            saveCommunication();
          }}
          isSaveCommunicationLoading={isSaveCommunicationLoading}
          isBulkCommunication={!!isBulkCommunication}
          saveCommunicationError={saveCommunicationError}
        />
      )}
      <Wrapper>
        {!!selectedServices && (
          <>
            <HeaderWithSteps
              title={t('processus', situationCategory)}
              subtitle={subtitle}
              step={{
                current: 2,
                total: 2,
                currentStepLabel: t('sendCommunication', 'step-2'),
              }}
              isStickyTop
              elemRef={headerRef}
            >
              <>
                <SelectionWrapper>
                  <div style={{ flex: 1 }}>
                    <ReminderSelections
                      elemRef={reminderSelectionsRef}
                      formValues={formValuesFirstStep}
                      isBulkCommunication={!!isBulkCommunication}
                    />
                  </div>
                  <div style={{ width: 300 }}>
                    <ServicesPassengersConcernedByCommunication
                      data={servicesPassengersConcernedByCommunicationData}
                      status={servicesPassengersConcernedByCommunicationStatus}
                      height={reminderSelectionsHeight}
                      step={2}
                      isNotAvailable={selectedServices && selectedServices?.length >= 5}
                    />
                  </div>
                </SelectionWrapper>
                <Col span="24">
                  <Row gutter={10}>
                    <Col span={12}>
                      <TargetSituation
                        height={targetChannelsHeight}
                        formik={formik}
                        selectStationsData={selectedSituations.data}
                        reasonsData={reasons.data}
                        isReasonActive={!!selectedMessageMask?.hasReason}
                        isOthersReason={!!reasons.isOthersReason}
                        handleSelectSituation={handleSelectSituation}
                        handleSelectReason={handleSelectReason}
                      />
                    </Col>
                    <Col span={12}>
                      <TargetChannels
                        hasTargetChannels={hasTargetChannels}
                        formik={formik}
                        elemRef={targetChannelsRef}
                        handleChannelChange={handleChannelChange}
                        channelsArePresent={communicationChannelsUtils.channelsArePresent}
                        canWriteDefferedCommunications={canWriteDefferedCommunications}
                        maxDaysBeforeDeferredSending={maxDaysBeforeDeferredSending}
                        targetChannelsHeight={targetChannelsHeight}
                        customersSelection={formValuesFirstStep.customersSelection}
                      />
                    </Col>
                  </Row>
                </Col>
                <VerticalSpace />
              </>
            </HeaderWithSteps>
            <StyledFormElems>
              <Row gutter={10}>
                <Col span={14}>
                  {isMailTargeted && (
                    <Col span="24">
                      <EmailContextualization formik={formik} />
                    </Col>
                  )}
                  <Col>
                    <ImpactedStations
                      formValuesFirstStep={formValuesFirstStep}
                      hasInformations={hasInformations}
                      stationsConcerned={stationsConcerned}
                      compensationSentencesData={compensationSentencesData}
                      selectedMessageMask={selectedMessageMask}
                      formik={formik}
                      defaultPrincipalZoneMessages={defaultPrincipalZoneMessages}
                      onFocusTextField={onFocusTextField}
                      ref={impactedStationsRef}
                      focusedTextFieldIndex={focusedTextFieldIndex?.contentIndex}
                      primaryEditorWrapperRef={primaryEditorWrapperRef}
                      secondaryEditorWrapperRef={secondaryEditorWrapperRef}
                      tertiaryEditorWrapperRef={tertiaryEditorWrapperRef}
                    />
                  </Col>
                  {selectedMessageMask?.hasSigningZone && (
                    <Col span="24">
                      <TargetSignature
                        formik={formik}
                        signatureValue={formik.values.signature || ''}
                        handleSignatureChange={formik.handleChange}
                      />
                    </Col>
                  )}
                  <ButtonWrapper>
                    <StyledBlueLink
                      noDecoration
                      backgroundColor={getTheme().colors.primary.light}
                      onClick={() => history.goBack()}
                      style={{
                        height: 40,
                        borderRadius: 20,
                        paddingRight: 15,
                        paddingLeft: 15,
                        fontSize: 15,
                      }}
                    >
                      {t('sendCommunication', 'backToPreviousStep')}
                    </StyledBlueLink>
                    <Button
                      height="40px"
                      width="auto"
                      borderRadius="20px"
                      disabled={!formik.isValid && Object.keys(formik.touched).length !== 0}
                      htmlType="submit"
                    >
                      {t('Buttons', 'TEMPLATE_BUTTON')}
                    </Button>
                  </ButtonWrapper>
                </Col>
                <Col span={10}>
                  <StickyBlock offsetTop={headerHeight}>
                    <MessageAssistant
                      data={dataMessageAssistant?.result}
                      defaultSituationId={formik.values.situationId}
                      situations={situations}
                      disabled={!isMessageAssistantActive}
                      headerHeight={headerHeight}
                      elemRef={messageAssistantRef}
                      serviceTypes={serviceTypes}
                      defaultServiceTypeID={selectedServiceTypeID}
                      getDataMessageAssisant={getDataMessageAssisant}
                      handleCopyText={handleCopyText}
                    />
                  </StickyBlock>
                </Col>
              </Row>
            </StyledFormElems>
          </>
        )}
      </Wrapper>
    </form>
  );
};

export default SendCommunicationStepTwo;
